import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

const ProtectedRoute = ({ children, requiredRole = null }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();
  const db = getFirestore();
  const location = useLocation();

  const checkUserAuthorization = useCallback(
    async (user) => {
      if (!user) {
        setIsAuthenticated(false);
        setIsAuthorized(false);
        setLoading(false);
        return;
      }

      setIsAuthenticated(true);

      try {
        if (requiredRole) {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnapshot = await getDoc(userDocRef).catch((err) => {
            throw new Error(`Failed to fetch user document: ${err.message}`);
          });

          if (userDocSnapshot.exists()) {
            const userData = userDocSnapshot.data();

            // Check if the role field exists; default to 'user' if not present
            const userRole = userData.role || 'user';
            const authorized = userRole === requiredRole;
            setIsAuthorized(authorized);

            if (!authorized) {
              setError(`Access denied. You must be an ${requiredRole} to access this page.`);
            } 
          } else {
            setIsAuthorized(false);
            setError('User profile not found. Please contact support.');
          }
        } else {
          setIsAuthorized(true);
        }
      } catch (err) {
        console.error('ProtectedRoute: Error checking user permissions:', err);
        setIsAuthorized(false);
        setError(`An error occurred while checking your permissions: ${err.message}`);
      } finally {
        setLoading(false);
      }
    },
    [requiredRole, db]
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, checkUserAuthorization);

    // Add a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (loading) {
        console.error('ProtectedRoute: Authentication check timed out after 10 seconds.');
        setLoading(false);
        setError('Authentication check timed out. Please try again.');
      }
    }, 10000); // 10 seconds timeout

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [auth, checkUserAuthorization, loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 p-6 rounded-lg shadow-md text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => (window.location.href = isAuthenticated ? '/dashboard/overview' : '/login')}
            className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300"
          >
            {isAuthenticated ? 'Back to Dashboard' : 'Go to Login'}
          </button>
        </div>
      </div>
    );
  }

  if (isAuthenticated && isAuthorized) {
    return children;
  } else if (isAuthenticated && !isAuthorized) {
    return <Navigate to="/dashboard/overview" replace state={{ from: location, error: `Access denied. You must be an ${requiredRole} to access this page.` }} />;
  } else {
    return <Navigate to="/login" replace state={{ from: location, message: 'Please log in to access this page' }} />;
  }
};

export default ProtectedRoute;